import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useGetTickets, useGetTicketsByFilter } from '~/entities/MyTickets/api';
import {
  apiTicketsToUITickets,
  filtersToApiParams,
} from '~/entities/MyTickets/mappers';
import { Ticket } from '~/entities/MyTickets/types';
import { TICKET_CONSTANTS } from '~/shared/constants';
import { MyTicketsFilters, ViewMode } from './types';

const { SEARCH, DATE } = TICKET_CONSTANTS;

interface MyTicketsContextType {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  filters: MyTicketsFilters;
  setFilters: (filters: Partial<MyTicketsFilters>) => void;
  clearFilters: () => void;
  clearIndividualFilter: (filterKey: keyof MyTicketsFilters) => void;
  hasActiveFilters: boolean;
  showDateFilters: boolean;
  setShowDateFilters: (show: boolean) => void;
  tickets: Ticket[];
  totalTickets: number;
  isLoading: boolean;
  error: any;
  errorState: { message: string; canRetry: boolean } | null;
  refetch: () => void;
  clearError: () => void;
}

const initialFilters: MyTicketsFilters = {
  search: '',
  category: [],
  zone: [],
  status: [],
  businessUnit: [],
  createdDateFrom: '',
  createdDateTo: '',
};

const MyTicketsContext = createContext<MyTicketsContextType | undefined>(
  undefined
);

export function MyTicketsProvider({ children }: { children: ReactNode }) {
  const [viewMode, setViewModeState] = useState<ViewMode>('list');
  const [filters, setFiltersState] = useState<MyTicketsFilters>(initialFilters);

  const setViewMode = useCallback((mode: ViewMode) => {
    setViewModeState(mode);
  }, []);
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [errorState, setErrorState] = useState<{
    message: string;
    canRetry: boolean;
  } | null>(null);

  const hasActiveFilters = useMemo(() => {
    const hasValidSearch = filters.search.length >= SEARCH.MIN_CHARACTERS;

    const hasValidDateFrom =
      filters.createdDateFrom.length === DATE.FORMAT_LENGTH;
    const hasValidDateTo = filters.createdDateTo.length === DATE.FORMAT_LENGTH;

    const hasOtherFilters = Object.entries(filters)
      .filter(
        ([key]) => !['search', 'createdDateFrom', 'createdDateTo'].includes(key)
      )
      .some(([, value]) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value.trim() !== '';
      });

    return (
      hasValidSearch || hasValidDateFrom || hasValidDateTo || hasOtherFilters
    );
  }, [filters]);

  const apiFilters = useMemo(() => filtersToApiParams(filters), [filters]);

  const allTicketsQuery = useGetTickets({
    enabled: !hasActiveFilters,
    onError: (error: any) => {
      const isAuthError = error?.response?.status === 401;
      setErrorState({
        message: isAuthError
          ? 'Authentication required. Please log in again.'
          : 'Failed to load tickets. Please try again.',
        canRetry: !isAuthError,
      });
    },
  });

  const filteredTicketsQuery = useGetTicketsByFilter(apiFilters, {
    enabled: hasActiveFilters,
    onError: (error: any) => {
      const isAuthError = error?.response?.status === 401;
      const isBadRequest = error?.response?.status === 400;

      let message = 'Failed to load filtered tickets. Please try again.';
      if (isAuthError) {
        message = 'Authentication required. Please log in again.';
      } else if (isBadRequest) {
        message =
          'Invalid filter parameters. Please check your filters and try again.';
      }

      setErrorState({
        message,
        canRetry: !isAuthError,
      });
    },
  });

  const activeQuery = hasActiveFilters ? filteredTicketsQuery : allTicketsQuery;

  const tickets = useMemo(() => {
    if (hasActiveFilters && filteredTicketsQuery.data) {
      return apiTicketsToUITickets(filteredTicketsQuery.data.tickets);
    }
    if (!hasActiveFilters && allTicketsQuery.data) {
      return apiTicketsToUITickets(allTicketsQuery.data);
    }
    return [];
  }, [hasActiveFilters, filteredTicketsQuery.data, allTicketsQuery.data]);

  const totalTickets = useMemo(() => {
    if (hasActiveFilters && filteredTicketsQuery.data) {
      return filteredTicketsQuery.data.total;
    }
    return tickets.length;
  }, [hasActiveFilters, filteredTicketsQuery.data, tickets.length]);

  const setFilters = useCallback((newFilters: Partial<MyTicketsFilters>) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }));
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState(initialFilters);
    setShowDateFilters(false);
  }, []);

  const clearIndividualFilter = useCallback(
    (filterKey: keyof MyTicketsFilters) => {
      if (filterKey === 'createdDateFrom' || filterKey === 'createdDateTo') {
        setFiltersState(prev => {
          const newFilters = { ...prev, [filterKey]: '' };
          if (!newFilters.createdDateFrom && !newFilters.createdDateTo) {
            setShowDateFilters(false);
          }
          return newFilters;
        });
      } else {
        setFiltersState(prev => ({ ...prev, [filterKey]: [] }));
      }
    },
    []
  );

  const refetch = useCallback(() => {
    setErrorState(null);
    if (hasActiveFilters) {
      filteredTicketsQuery.refetch();
    } else {
      allTicketsQuery.refetch();
    }
  }, [hasActiveFilters, filteredTicketsQuery, allTicketsQuery]);

  const clearError = useCallback(() => {
    setErrorState(null);
  }, []);

  const contextValue: MyTicketsContextType = useMemo(
    () => ({
      viewMode,
      setViewMode,
      filters,
      setFilters,
      clearFilters,
      clearIndividualFilter,
      hasActiveFilters,
      showDateFilters,
      setShowDateFilters,
      tickets,
      totalTickets,
      isLoading: activeQuery.isLoading,
      error: activeQuery.error,
      errorState,
      refetch,
      clearError,
    }),
    [
      viewMode,
      setViewMode,
      filters,
      setFilters,
      clearFilters,
      clearIndividualFilter,
      hasActiveFilters,
      showDateFilters,
      setShowDateFilters,
      tickets,
      totalTickets,
      activeQuery.isLoading,
      activeQuery.error,
      errorState,
      refetch,
      clearError,
    ]
  );

  return (
    <MyTicketsContext.Provider value={contextValue}>
      {children}
    </MyTicketsContext.Provider>
  );
}

export function useMyTickets() {
  const context = useContext(MyTicketsContext);
  if (!context) {
    throw new Error('useMyTickets must be used within MyTicketsProvider');
  }
  return context;
}
